// src/features/dashboard/pages/LoginScreen.tsx

import {
  useEffect,
  useRef,
} from 'react';

import LoginBanner from '../../auth/components/Login/LoginBanner';

import LoginHeader from '../components/Login/LoginHeader';
import LoginForm from '../components/Login/LoginForm';
import SsoLoginCard from '../components/Login/SsoLoginCard';

import useLogin from '../hooks/Login/useLogin';
import useSsoLogin from '../hooks/Login/useSsoLogin';

import {
  useLoaderContext,
} from '../../../components/Loader/LoaderContext';

export default function LoginScreen() {
  /* ============================================================
     GLOBAL LOADER
  ============================================================ */

  const {
    hideLoader,
  } = useLoaderContext();

  /* ============================================================
     LOGIN HOOK
  ============================================================ */

  const {
    employeeCode,
    setEmployeeCode,
    loading,
    error,
    handleLogin,
  } = useLogin();

  /* ============================================================
     SSO LOGIN HOOK
  ============================================================ */

  const {
    showSSO,
    setShowSSO,
    ssoEmployeeCode,
    setSsoEmployeeCode,
    ssoLoading,
    handleSSOLogin,
  } = useSsoLogin();

  /* ============================================================
     LOADER TIMER
  ============================================================ */

  const loaderTimerRef =
    useRef<number | null>(null);

  /* ============================================================
     STOP GLOBAL LOADER
  ============================================================ */

  useEffect(() => {
    const isLoading =
      loading ||
      ssoLoading;

    if (isLoading) {
      return;
    }

    if (
      loaderTimerRef.current !== null
    ) {
      window.clearTimeout(
        loaderTimerRef.current,
      );

      loaderTimerRef.current = null;
    }

    loaderTimerRef.current =
      window.setTimeout(() => {
        hideLoader();

        loaderTimerRef.current =
          null;
      }, 100);

    return () => {
      if (
        loaderTimerRef.current !== null
      ) {
        window.clearTimeout(
          loaderTimerRef.current,
        );

        loaderTimerRef.current = null;
      }
    };
  }, [
    loading,
    ssoLoading,
    hideLoader,
  ]);

  /* ============================================================
     UNMOUNT CLEANUP
  ============================================================ */

  useEffect(() => {
    return () => {
      if (
        loaderTimerRef.current !== null
      ) {
        window.clearTimeout(
          loaderTimerRef.current,
        );

        loaderTimerRef.current = null;
      }

      hideLoader();
    };
  }, [
    hideLoader,
  ]);

  /* ============================================================
     RENDER
  ============================================================ */

  return (
    <div
      className="
        flex

        min-h-[100dvh]

        w-full

        overflow-hidden

        bg-slate-50
      "
    >
      {/* ========================================================
          LEFT LOGIN BANNER
      ======================================================== */}

      <LoginBanner />

      {/* ========================================================
          LOGIN CONTENT
      ======================================================== */}

      <div
        className="
          flex
          min-w-0
          flex-1

          items-center
          justify-center

          overflow-y-auto

          px-[clamp(0.75rem,1.5vw,1.5rem)]

          py-[clamp(0.75rem,1.5vw,1.5rem)]
        "
      >
        {/* ======================================================
            LOGIN CARD CONTAINER
        ====================================================== */}

        <div
          className="
            w-full

            max-w-[clamp(320px,30vw,400px)]

            min-w-0
          "
        >
          {/* ====================================================
              LOGIN CARD
          ==================================================== */}

          <div
            className="
              w-full

              overflow-hidden

              rounded-[clamp(1rem,1.8vw,1.5rem)]

              border
              border-slate-100

              bg-white

              px-[clamp(0.875rem,1.5vw,1.5rem)]

              py-[clamp(0.875rem,1.5vw,1.5rem)]

              shadow-lg
            "
          >
            {/* ==================================================
                HEADER
            ================================================== */}

            <LoginHeader />

            {/* ==================================================
                NORMAL LOGIN
            ================================================== */}

            <LoginForm
              employeeCode={
                employeeCode
              }

              loading={
                loading
              }

              error={
                error
              }

              onEmployeeCodeChange={
                setEmployeeCode
              }

              onSubmit={
                handleLogin
              }
            />

            {/* ==================================================
                SSO LOGIN
            ================================================== */}

            <SsoLoginCard
              showSSO={
                showSSO
              }

              employeeCode={
                ssoEmployeeCode
              }

              loading={
                ssoLoading
              }

              error={
                error
              }

              onToggle={() =>
                setShowSSO(
                  !showSSO,
                )
              }

              onEmployeeCodeChange={
                setSsoEmployeeCode
              }

              onSubmit={
                handleSSOLogin
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}